import { useState, FormEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { useConfig } from "../context/ConfigContext";
import { OAuthButtons, OAuthDivider } from "../components/OAuthButtons";
import {
  colors, fontSizes, radii, transitions,
  centeredPageStyle, authCardStyle,
  baseInputStyle, baseInputFocusStyle,
  primaryBtnStyle, primaryBtnHoverStyle,
} from "../styles/theme";

const COGNITO_ENDPOINT = "https://cognito-idp.ap-southeast-2.amazonaws.com/";

async function cognitoAuth(username: string, password: string, clientId: string) {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
    },
    body: JSON.stringify({
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: clientId,
      AuthParameters: { USERNAME: username, PASSWORD: password },
    }),
  });
  const body = await res.json();
  if (!res.ok) {
    const msg: string = body.message ?? body.__type ?? `Auth failed (${res.status})`;
    if (body.__type === "UserNotConfirmedException") throw new Error("USER_NOT_CONFIRMED");
    if (body.__type === "NotAuthorizedException" || /incorrect|not authorized/i.test(msg)) {
      throw new Error("Invalid email or password");
    }
    throw new Error(msg);
  }
  return body;
}

export function FleetLogin() {
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [error,        setError]        = useState<string | null>(null);
  const [loading,      setLoading]      = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [btnHover,     setBtnHover]     = useState(false);
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const brand     = useBrand();
  const { clientId } = useConfig();
  const from = (location.state as { from?: string } | null)?.from ?? "/fleet/board";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await cognitoAuth(email, password, clientId);
      const result = data.AuthenticationResult;
      if (!result?.IdToken) throw new Error("No token received");
      login(result.IdToken, result.RefreshToken ?? "");
      navigate(from);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Sign-in failed";
      if (msg === "USER_NOT_CONFIRMED") {
        sessionStorage.setItem("pending_verify_email", email);
        navigate("/signup/verify");
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const sessionExpired = (location.state as { reason?: string } | null)?.reason === "expired";
  const justVerified    = (location.state as { verified?: boolean } | null)?.verified === true;
  const passwordReset   = (location.state as { passwordReset?: boolean } | null)?.passwordReset === true;

  return (
    <div style={{ ...centeredPageStyle }}>
      <form onSubmit={handleSubmit} style={authCardStyle}>
        <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
          <img
            src={brand.logo_url}
            alt={brand.name}
            style={{ height: 64, marginBottom: "0.75rem" }}
          />
          <h2 style={{
            color: brand.accent_color,
            fontSize: fontSizes.xl,
            fontWeight: 700,
            margin: 0,
            letterSpacing: "-0.01em",
          }}>
            {brand.name}
          </h2>
          <p style={{ color: colors.textMuted, fontSize: fontSizes.sm, margin: "0.25rem 0 0" }}>
            {brand.login_intro}
          </p>
        </div>

        {passwordReset && (
          <div style={{
            background: "rgba(76,175,136,0.12)",
            border: `1px solid ${colors.green}`,
            borderRadius: radii.sm,
            padding: "0.6rem 0.9rem",
            color: colors.green,
            fontSize: fontSizes.sm,
            textAlign: "center",
          }}>
            Password updated — sign in with your new password.
          </div>
        )}

        {justVerified && (
          <div style={{
            background: "rgba(76,175,136,0.12)",
            border: `1px solid ${colors.green}`,
            borderRadius: radii.sm,
            padding: "0.6rem 0.9rem",
            color: colors.green,
            fontSize: fontSizes.sm,
            textAlign: "center",
          }}>
            Account confirmed — sign in below.
          </div>
        )}

        {sessionExpired && (
          <div style={{
            background: "rgba(245,166,35,0.10)",
            border: `1px solid rgba(245,166,35,0.35)`,
            borderRadius: radii.sm,
            padding: "0.6rem 0.9rem",
            color: colors.goldSecondary,
            fontSize: fontSizes.sm,
            textAlign: "center",
          }}>
            Your session has expired — please sign in again.
          </div>
        )}

        {error && (
          <div style={{
            background: "rgba(232,140,0,0.12)",
            border: `1px solid ${colors.warning}`,
            borderRadius: radii.sm,
            padding: "0.6rem 0.9rem",
            color: colors.warning,
            fontSize: fontSizes.sm,
          }}>
            {error}
          </div>
        )}

        {/* #2316 — Google + GitHub one-click sign-in */}
        <OAuthButtons mode="signin" />
        <OAuthDivider />

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
            Email
          </label>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            onFocus={() => setFocusedField("email")}
            onBlur={() => setFocusedField(null)}
            style={focusedField === "email" ? baseInputFocusStyle : baseInputStyle}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
              Password
            </label>
            <Link
              to="/forgot-password"
              style={{ color: colors.textMuted, fontSize: fontSizes.xs, textDecoration: "underline" }}
            >
              Forgot password?
            </Link>
          </div>
          <input
            type="password"
            placeholder="••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            onFocus={() => setFocusedField("password")}
            onBlur={() => setFocusedField(null)}
            style={focusedField === "password" ? baseInputFocusStyle : baseInputStyle}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          style={{
            ...(btnHover && !loading ? primaryBtnHoverStyle : primaryBtnStyle),
            marginTop: "0.3rem",
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
            transition: transitions.fast,
          }}
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <p style={{ textAlign: "center", color: colors.textMuted, fontSize: fontSizes.sm, margin: 0 }}>
          No account?{" "}
          <Link to="/signup" style={{ color: colors.goldSecondary, textDecoration: "none" }}>
            Sign up
          </Link>
        </p>

        <p style={{ textAlign: "center", color: colors.textMuted, fontSize: fontSizes.xs, margin: 0 }}>
          Not verified yet?{" "}
          <Link to="/signup/verify" style={{ color: colors.textMuted, textDecoration: "underline" }}>
            Enter your code →
          </Link>
        </p>

        {/* #2318 — magic-link login shortcut */}
        <p style={{ textAlign: "center", color: colors.textMuted, fontSize: fontSizes.xs, margin: 0 }}>
          Prefer a sign-in link?{" "}
          <Link to="/auth/magic-link" style={{ color: colors.textMuted, textDecoration: "underline" }}>
            Email me a link →
          </Link>
        </p>

        <p style={{ textAlign: "center", fontSize: "0.7rem", color: colors.textMuted, opacity: 0.5, margin: 0, display: "flex", gap: 12, justifyContent: "center" }}>
          <a href="/privacy" style={{ color: "inherit", textDecoration: "none" }}>Privacy</a>
          <a href="/terms"   style={{ color: "inherit", textDecoration: "none" }}>Terms</a>
        </p>
      </form>
    </div>
  );
}
